import React, { useEffect, useState } from "react"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import MaintenanceBadge from "../../components/Common/MaintenanceBadge"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import PlatformsApi from "../../services/platformsApi"
import "./popular.css"

/**
 * Two independent sections (Most Popular, Pre Orders), each using the same
 * `InsightsPageHeader` as every other Weekly Insights page (Trending, Top Apps, Top
 * Discounts, Pre-Orders, …) — platform pills next to the title, country/language
 * pickers, filters, and rank sort — instead of the bare title + a plain "sort by
 * revenue/downloads" `<select>` this page had before. Platform selection is shared
 * across both sections on this page and lives in the `?platform=` URL query param
 * (defaulting to Meta) so it survives a round trip to an app's detail page and back;
 * country/language are independent per section, same as the standalone Pre-Orders
 * and Most-Popular pages.
 */
export default function Popular() {
  const { filterSections } = useFilters()

  // Shared across both sections on this page only.
  const [platforms, setPlatforms] = useState([])
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(null)

  // Most Popular section
  const [mostPopular, setMostPopular] = useState([])
  const [mostPopularCountry, setMostPopularCountry] = useState(() => {
    try { return sessionStorage.getItem('mostPopular_selectedCountry') || 'US' } catch (e) { return 'US' }
  })
  const [mostPopularLanguage, setMostPopularLanguage] = useState(() => {
    try { return sessionStorage.getItem('mostPopular_selectedLanguage') } catch (e) { return null }
  })
  const [mostPopularLastUpdated, setMostPopularLastUpdated] = useState(null)
  const [mostFilterPanelOpen, setMostFilterPanelOpen] = useState(false)
  const [mostAppliedFilters, setMostAppliedFilters] = useState({})
  const [mostLoading, setMostLoading] = useState(true)
  const [mostItemsToShow, setMostItemsToShow] = useState(4)
  const [mostShowAll, setMostShowAll] = useState(false)
  const [mostSortedItems, setMostSortedItems] = useState([])
  const [mostIsSorted, setMostIsSorted] = useState(false)
  const [mostRankSort, setMostRankSort] = useState(null)

  // Pre Orders section
  const [preOrders, setPreOrders] = useState([])
  const [preOrdersCountry, setPreOrdersCountry] = useState(() => {
    try { return sessionStorage.getItem('popular_preOrders_selectedCountry') || 'US' } catch (e) { return 'US' }
  })
  const [preOrdersLanguage, setPreOrdersLanguage] = useState(() => {
    try { return sessionStorage.getItem('popular_preOrders_selectedLanguage') } catch (e) { return null }
  })
  const [preOrdersLastUpdated, setPreOrdersLastUpdated] = useState(null)
  const [preFilterPanelOpen, setPreFilterPanelOpen] = useState(false)
  const [preAppliedFilters, setPreAppliedFilters] = useState({})
  const [preLoading, setPreLoading] = useState(true)
  const [preItemsToShow, setPreItemsToShow] = useState(4)
  const [preShowAll, setPreShowAll] = useState(false)
  const [preSortedItems, setPreSortedItems] = useState([])
  const [preIsSorted, setPreIsSorted] = useState(false)
  const [preRankSort, setPreRankSort] = useState(null)

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (const c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && app.meta.rank !== undefined) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const fetchMostPopular = async (
    country = mostPopularCountry,
    language = mostPopularLanguage,
    filters = mostAppliedFilters,
    platform = selectedPlatform
  ) => {
    try {
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        else if (filters.sort) params.sort_by = filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/most_popular`, { params, headers })
      const games = res.data.games || []
      setMostPopular(games)
      try { sessionStorage.setItem("popularMeta", JSON.stringify(games)) } catch (e) { /* ignore */ }
      setMostPopularLastUpdated(new Date())
    } catch (error) {
      console.error("Error fetching most popular:", error)
    } finally {
      setMostLoading(false)
    }
  }

  const fetchPreOrders = async (
    country = preOrdersCountry,
    language = preOrdersLanguage,
    filters = preAppliedFilters,
    platform = selectedPlatform
  ) => {
    try {
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        else if (filters.sort) params.sort_by = filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/coming_soon_pre_orders`, { params, headers })
      const games = res.data.games || []
      setPreOrders(games)
      try { sessionStorage.setItem("preOrderMeta", JSON.stringify(games)) } catch (e) { /* ignore */ }
      setPreOrdersLastUpdated(new Date())
    } catch (error) {
      console.error("Error fetching pre orders:", error)
    } finally {
      setPreLoading(false)
    }
  }

  useEffect(() => {
    const loadCountriesAndPlatforms = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        list = countriesRes.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
      } catch (err) {
        console.error('Error loading countries/platforms:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
      } finally {
        setCountriesLoading(false)
        setPlatformsLoading(false)

        const firstLocaleFor = (countryCode) => {
          const entry = list.find(x => x.code === countryCode)
          return entry && Array.isArray(entry.locales) && entry.locales.length ? entry.locales[0] : null
        }

        const initMostLanguage = mostPopularLanguage || firstLocaleFor(mostPopularCountry)
        const initPreLanguage = preOrdersLanguage || firstLocaleFor(preOrdersCountry)
        setMostPopularLanguage(initMostLanguage)
        setPreOrdersLanguage(initPreLanguage)

        setMostLoading(true)
        setPreLoading(true)
        fetchMostPopular(mostPopularCountry, initMostLanguage, mostAppliedFilters)
        fetchPreOrders(preOrdersCountry, initPreLanguage, preAppliedFilters)
      }
    }

    loadCountriesAndPlatforms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    setMostLoading(true)
    setPreLoading(true)
    fetchMostPopular(mostPopularCountry, mostPopularLanguage, mostAppliedFilters, platformId)
    fetchPreOrders(preOrdersCountry, preOrdersLanguage, preAppliedFilters, platformId)
  }

  const handleMostCountryChange = (code, langForCountry) => {
    setMostPopularCountry(code)
    setMostPopularLanguage(langForCountry)
    try {
      sessionStorage.setItem('mostPopular_selectedCountry', code)
      sessionStorage.setItem('mostPopular_selectedLanguage', langForCountry || '')
    } catch (e) { /* ignore */ }
    setMostLoading(true)
    fetchMostPopular(code, langForCountry, mostAppliedFilters)
  }

  const handleMostLanguageChange = (val) => {
    setMostPopularLanguage(val)
    try { sessionStorage.setItem('mostPopular_selectedLanguage', val || '') } catch (e) { /* ignore */ }
    setMostLoading(true)
    fetchMostPopular(mostPopularCountry, val, mostAppliedFilters)
  }

  const handlePreCountryChange = (code, langForCountry) => {
    setPreOrdersCountry(code)
    setPreOrdersLanguage(langForCountry)
    try {
      sessionStorage.setItem('popular_preOrders_selectedCountry', code)
      sessionStorage.setItem('popular_preOrders_selectedLanguage', langForCountry || '')
    } catch (e) { /* ignore */ }
    setPreLoading(true)
    fetchPreOrders(code, langForCountry, preAppliedFilters)
  }

  const handlePreLanguageChange = (val) => {
    setPreOrdersLanguage(val)
    try { sessionStorage.setItem('popular_preOrders_selectedLanguage', val || '') } catch (e) { /* ignore */ }
    setPreLoading(true)
    fetchPreOrders(preOrdersCountry, val, preAppliedFilters)
  }

  const handleMostFilterApply = (filters) => {
    setMostAppliedFilters(filters)
    setMostLoading(true)
    fetchMostPopular(mostPopularCountry, mostPopularLanguage, filters)
    setMostFilterPanelOpen(false)
  }

  const handleRemoveMostFilter = (key) => {
    const newFilters = { ...(mostAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setMostAppliedFilters(newFilters)
      setMostLoading(true)
      fetchMostPopular(mostPopularCountry, mostPopularLanguage, newFilters)
    }
  }

  const handlePreFilterApply = (filters) => {
    setPreAppliedFilters(filters)
    setPreLoading(true)
    fetchPreOrders(preOrdersCountry, preOrdersLanguage, filters)
    setPreFilterPanelOpen(false)
  }

  const handleRemovePreFilter = (key) => {
    const newFilters = { ...(preAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setPreAppliedFilters(newFilters)
      setPreLoading(true)
      fetchPreOrders(preOrdersCountry, preOrdersLanguage, newFilters)
    }
  }

  const toggleMostRankSort = () => setMostRankSort(prev => (prev === 'asc' ? 'desc' : 'asc'))
  const togglePreRankSort = () => setPreRankSort(prev => (prev === 'asc' ? 'desc' : 'asc'))

  useEffect(() => {
    if (!mostRankSort) { setMostIsSorted(false); setMostSortedItems([]); return }
    const arr = [...mostPopular].sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return mostRankSort === 'asc' ? ra - rb : rb - ra
    })
    setMostSortedItems(arr)
    setMostIsSorted(true)
  }, [mostPopular, mostRankSort])

  useEffect(() => {
    if (!preRankSort) { setPreIsSorted(false); setPreSortedItems([]); return }
    const arr = [...preOrders].sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return preRankSort === 'asc' ? ra - rb : rb - ra
    })
    setPreSortedItems(arr)
    setPreIsSorted(true)
  }, [preOrders, preRankSort])

  useEffect(() => {
    setMostItemsToShow(mostShowAll ? mostPopular.length : 4)
  }, [mostShowAll, mostPopular.length])

  useEffect(() => {
    setPreItemsToShow(preShowAll ? preOrders.length : 4)
  }, [preShowAll, preOrders.length])

  const handleCardClick = (cardId) => {
    setSelectedCardId(cardId)
  }

  const displayMostPopular = (mostIsSorted ? mostSortedItems : mostPopular).slice(0, mostItemsToShow)
  const displayPreOrders = (preIsSorted ? preSortedItems : preOrders).slice(0, preItemsToShow)

  // Disclaimer references whichever store is currently selected, rather than
  // always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <div className="page-content popular-page">
        {/* Most Popular Section */}
        <div className="popular-section">
          <InsightsPageHeader
            title={
              <>
                Most Popular
                <MaintenanceBadge style={{ position: "relative", display: "inline-block", marginLeft: 6, padding: "4px 8px", fontSize: 12, borderRadius: 6, background: "rgba(255,165,0,0.95)", color: "#111", boxShadow: "0 1px 6px rgba(0,0,0,0.12)", zIndex: 2 }}>
                  Under Maintenance
                </MaintenanceBadge>
              </>
            }
            description="Dive into the most popular apps of the week, combining app score, downloads, and user engagement metrics."
            disclaimer={`Insights are based on ${platformLabel} Store rankings.`}
            platforms={platforms}
            platformsLoading={platformsLoading}
            selectedPlatform={selectedPlatform}
            onPlatformChange={handlePlatformChange}
            countriesList={countriesList}
            countryOptions={countryOptions}
            countriesLoading={countriesLoading}
            selectedCountry={mostPopularCountry}
            selectedLanguage={mostPopularLanguage}
            onCountryChange={handleMostCountryChange}
            onLanguageChange={handleMostLanguageChange}
            appliedFilters={mostAppliedFilters}
            onRemoveFilter={handleRemoveMostFilter}
            lastUpdated={mostPopularLastUpdated}
            onOpenFilters={() => setMostFilterPanelOpen(true)}
            rankSort={mostRankSort}
            onToggleSort={toggleMostRankSort}
          />
          <FilterPanel
            open={mostFilterPanelOpen}
            sections={filterSections.length > 0 ? filterSections : undefined}
            // Platform is already selectable via the Meta Quest/Steam tabs above -
            // don't duplicate it inside the filter panel.
            hidePlatformSection
            onClose={() => setMostFilterPanelOpen(false)}
            onApply={handleMostFilterApply}
            initialValues={mostAppliedFilters}
          />

          {/* Content Area */}
          <div className="popular-content">
            {mostLoading ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="popular-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayMostPopular.length > 0 ? (
                  <Row className="g-3">
                    {displayMostPopular.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`popular-card ${selectedCardId === app?.item_id ? 'selected' : ''}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          <TopAppCardM gamedata={app} priceLayout="discounts" showEnds={false} headingLevel="h2" platformIcon={platformIconUrl} platformName={platformLabel} />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(mostAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {!mostLoading && (
                  <div className="text-center mt-4">
                    {!mostShowAll && mostPopular.length > 4 ? (
                      <Button className="see-more-btn" onClick={() => setMostShowAll(true)}>
                        See More
                      </Button>
                    ) : mostShowAll ? (
                      <Button className="see-less-btn" onClick={() => setMostShowAll(false)}>
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="section-divider"></div>

        {/* Pre Orders Section */}
        <div className="popular-section">
          <InsightsPageHeader
            title={
              <>
                Pre Orders
                <MaintenanceBadge style={{ position: "relative", display: "inline-block", marginLeft: 6, padding: "4px 8px", fontSize: 12, borderRadius: 6, background: "rgba(255,165,0,0.95)", color: "#111", boxShadow: "0 1px 6px rgba(0,0,0,0.12)", zIndex: 2 }}>
                  Under Maintenance
                </MaintenanceBadge>
              </>
            }
            description="Browse the top pre-order apps available this week — and be first to experience them."
            disclaimer={`Insights are based on ${platformLabel} Store rankings and verified pre-order data.`}
            platforms={platforms}
            platformsLoading={platformsLoading}
            selectedPlatform={selectedPlatform}
            onPlatformChange={handlePlatformChange}
            countriesList={countriesList}
            countryOptions={countryOptions}
            countriesLoading={countriesLoading}
            selectedCountry={preOrdersCountry}
            selectedLanguage={preOrdersLanguage}
            onCountryChange={handlePreCountryChange}
            onLanguageChange={handlePreLanguageChange}
            appliedFilters={preAppliedFilters}
            onRemoveFilter={handleRemovePreFilter}
            lastUpdated={preOrdersLastUpdated}
            onOpenFilters={() => setPreFilterPanelOpen(true)}
            rankSort={preRankSort}
            onToggleSort={togglePreRankSort}
          />
          <FilterPanel
            open={preFilterPanelOpen}
            sections={filterSections.length > 0 ? filterSections : undefined}
            // Platform is already selectable via the Meta Quest/Steam tabs above -
            // don't duplicate it inside the filter panel.
            hidePlatformSection
            onClose={() => setPreFilterPanelOpen(false)}
            onApply={handlePreFilterApply}
            initialValues={preAppliedFilters}
          />

          {/* Content Area */}
          <div className="popular-content">
            {preLoading ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="popular-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayPreOrders.length > 0 ? (
                  <Row className="g-3">
                    {displayPreOrders.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`popular-card ${selectedCardId === app?.item_id ? 'selected' : ''}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          {/* Pre-order apps haven't launched yet, so there's no real revenue or
                              downloads figure to show — hide both fields instead of a misleading
                              fallback. "Released" (a future date) still makes sense to show. */}
                          <TopAppCardM gamedata={app} priceLayout="discounts" showEnds={false} showRevenue={false} showDownloads={false} headingLevel="h2" platformIcon={platformIconUrl} platformName={platformLabel} />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(preAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {!preLoading && (
                  <div className="text-center mt-4">
                    {!preShowAll && preOrders.length > 4 ? (
                      <Button className="see-more-btn" onClick={() => setPreShowAll(true)}>
                        See More
                      </Button>
                    ) : preShowAll ? (
                      <Button className="see-less-btn" onClick={() => setPreShowAll(false)}>
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}
